import React from 'react'
import { Card, Stepper, Box, Button } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ProgressBarSteps } from '../../types'
import BottomProgressBarStep from './BottomProgressBarStep'
import SaveConfigurator from './SaveConfigurator'

interface BottomProgressBarProps {
  handleBack?: boolean
  progress: number
  onStepChange?: (progress: ProgressBarSteps) => void
  onPrimaryAction: () => void
  allProductsSelected?: boolean
  configuratorId: string | null
  primaryButtonLabel: string
  showSaveConfigurator?: boolean
}

const useStyles = makeStyles()(theme => ({
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  connector: {
    width: '12px',
    height: '1px',
    borderBottom: '1px solid' + theme.palette.primary.main,
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    transform: 'translateY(-12px)',
  },
}))

const BottomProgressBar: React.FC<BottomProgressBarProps> = ({
  handleBack,
  progress,
  onStepChange,
  onPrimaryAction,
  allProductsSelected,
  configuratorId,
  primaryButtonLabel,
  showSaveConfigurator = true,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const isPrimaryDisabled =
    (progress === ProgressBarSteps.BasicSystem && configuratorId === null) || (progress === ProgressBarSteps.MainComponent && !allProductsSelected)

  return (
    <Box sx={{ width: '100%' }}>
      <Card className={classes.spaceBetween}>
        <Stepper>
          <BottomProgressBarStep
            isStepActive={progress === ProgressBarSteps.BasicSystem}
            isStepCompleted={progress > ProgressBarSteps.BasicSystem}
            stepNumber={ProgressBarSteps.BasicSystem}
            stepLabel={t('shop.progress-bar.basic-system')}
            onStepChange={() => onStepChange?.(ProgressBarSteps.BasicSystem)}
          />

          <Box className={classes.connector} />

          <BottomProgressBarStep
            isStepActive={progress === ProgressBarSteps.MainComponent}
            isStepCompleted={progress > ProgressBarSteps.MainComponent}
            stepNumber={ProgressBarSteps.MainComponent}
            stepLabel={t('shop.progress-bar.main-component')}
            onStepChange={() => progress > ProgressBarSteps.MainComponent && onStepChange?.(ProgressBarSteps.MainComponent)}
          />

          <Box className={classes.connector} />

          <BottomProgressBarStep
            isStepActive={progress === ProgressBarSteps.AdditionalComponent}
            isStepCompleted={progress >= ProgressBarSteps.AdditionalComponent}
            stepNumber={ProgressBarSteps.AdditionalComponent}
            stepLabel={t('shop.progress-bar.additional-component')}
            onStepChange={() => progress > ProgressBarSteps.AdditionalComponent && onStepChange?.(ProgressBarSteps.AdditionalComponent)}
          />
        </Stepper>

        {handleBack && (
          <Box>
            {showSaveConfigurator && <SaveConfigurator step={progress} />}

            <Button disabled={isPrimaryDisabled} onClick={onPrimaryAction} sx={{ padding: '22px 30px' }} size="large" variant="contained">
              {primaryButtonLabel}
            </Button>
          </Box>
        )}
      </Card>
    </Box>
  )
}

export default BottomProgressBar
